import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeaders, mapErrorMessage, buildFolderTree } from '../../lib/libraryUtils';

/**
 * Hook for fetching library data (folders, documents, trash, audit)
 */
export const useLibraryData = () => {
  // Folders state
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Trash state
  const [trashFolders, setTrashFolders] = useState([]);
  const [trashDocuments, setTrashDocuments] = useState([]);
  const [trashSummary, setTrashSummary] = useState({ total_items: 0, failed_deletes: 0 });
  const [loadingTrash, setLoadingTrash] = useState(false);

  // Audit state
  const [auditEvents, setAuditEvents] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Fetch folders
  const fetchFolders = useCallback(async () => {
    try {
      setLoadingFolders(true);
      const response = await axios.get(`${API}/library/folders`, getAuthHeaders());
      const allFolders = response.data || [];
      setFolders(allFolders);
      setFolderTree(buildFolderTree(allFolders));
    } catch (error) {
      console.error('Error fetching folders:', error);
      toast.error(mapErrorMessage(error, 'ophalen mappen'));
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  // Fetch documents
  const fetchDocuments = useCallback(async (folderId = null, search = '', extension = 'all', limit = 50, skip = 0) => {
    try {
      setLoadingDocuments(true);
      const params = new URLSearchParams();
      if (folderId) params.append('folder_id', folderId);
      if (search) params.append('search', search);
      if (extension && extension !== 'all') params.append('extension', extension);
      params.append('limit', limit.toString());
      params.append('skip', skip.toString());

      const response = await axios.get(
        `${API}/library/documents?${params.toString()}`,
        getAuthHeaders()
      );
      
      setDocuments(response.data.documents || []);
      setTotalDocuments(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error(mapErrorMessage(error, 'ophalen documenten'));
    } finally {
      setLoadingDocuments(false);
    }
  }, []);

  // Fetch trash
  const fetchTrash = useCallback(async () => {
    try {
      setLoadingTrash(true);
      const response = await axios.get(`${API}/library/trash`, getAuthHeaders());
      setTrashFolders(response.data.folders || []);
      setTrashDocuments(response.data.documents || []);
      setTrashSummary(response.data.summary || { total_items: 0, failed_deletes: 0 });
    } catch (error) {
      console.error('Error fetching trash:', error);
      toast.error(mapErrorMessage(error, 'ophalen prullenbak'));
    } finally {
      setLoadingTrash(false);
    }
  }, []);

  // Fetch audit
  const fetchAudit = useCallback(async (documentId = null, limit = 100, skip = 0) => {
    try {
      setLoadingAudit(true);
      const params = new URLSearchParams();
      if (documentId) params.append('document_id', documentId);
      params.append('limit', limit.toString());
      params.append('skip', skip.toString());

      const response = await axios.get(
        `${API}/library/audit?${params.toString()}`,
        getAuthHeaders()
      );
      setAuditEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching audit:', error);
      toast.error(mapErrorMessage(error, 'ophalen audit log'));
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([fetchFolders(), fetchDocuments()]);
  }, [fetchFolders, fetchDocuments]);

  return {
    // Folders
    folders,
    folderTree,
    loadingFolders,
    fetchFolders,
    setFolders,

    // Documents
    documents,
    totalDocuments,
    loadingDocuments,
    fetchDocuments,
    setDocuments,

    // Trash
    trashFolders,
    trashDocuments,
    trashSummary,
    loadingTrash,
    fetchTrash,

    // Audit
    auditEvents,
    loadingAudit,
    fetchAudit,

    // Helpers
    refreshAll,
  };
};

export default useLibraryData;
